import React, { useState, useEffect, useCallback } from 'react';
import { api } from '@/api';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Lock, Plus, Search, FileText, Image, Video, File, Shield, Tag, Trash2 } from 'lucide-react';

const TYPE_ICONS = {
  witness_statement: FileText, correspondence: FileText, photo: Image,
  contract_doc: File, expert_report: FileText, minutes: FileText,
  programme: FileText, financial: FileText, video: Video, other: File,
};
const TYPE_COLORS = {
  witness_statement: 'bg-blue-100 text-blue-700', correspondence: 'bg-purple-100 text-purple-700',
  photo: 'bg-green-100 text-green-700', contract_doc: 'bg-slate-100 text-slate-700',
  expert_report: 'bg-amber-100 text-amber-700', minutes: 'bg-cyan-100 text-cyan-700',
  programme: 'bg-indigo-100 text-indigo-700', financial: 'bg-rose-100 text-rose-700',
  video: 'bg-teal-100 text-teal-700', other: 'bg-slate-100 text-slate-600',
};
const EVIDENCE_TYPES = [
  'witness_statement', 'correspondence', 'photo', 'contract_doc',
  'expert_report', 'minutes', 'programme', 'financial', 'video', 'other',
];

export default function EvidenceLocker({ matterId, issues }) {
  const [evidence, setEvidence] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [form, setForm] = useState({
    title: '', description: '', evidence_type: 'other', issue_id: '',
    date_of_evidence: '', tags: '', privileged_flag: false,
  });

  const loadData = useCallback(async () => {
    try {
      const res = await api.disputes.listEvidence(matterId);
      setEvidence(res.data || []);
    } catch { /* silent */ } finally { setLoading(false); }
  }, [matterId]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleAdd = async () => {
    if (!form.title.trim()) { toast.error('Title required'); return; }
    try {
      await api.disputes.createEvidence(matterId, {
        ...form, matter_id: matterId,
        tags: form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
      });
      toast.success('Evidence added');
      setShowAdd(false);
      setForm({ title: '', description: '', evidence_type: 'other', issue_id: '', date_of_evidence: '', tags: '', privileged_flag: false });
      loadData();
    } catch { toast.error('Failed to add evidence'); }
  };

  const handleDelete = async (id) => {
    try {
      await api.disputes.deleteEvidence(id);
      toast.success('Evidence removed');
      loadData();
    } catch { toast.error('Delete failed'); }
  };

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin h-8 w-8 border-2 border-slate-300 border-t-slate-800 rounded-full" /></div>;

  const filtered = evidence.filter(e => {
    if (filterType && e.evidence_type !== filterType) return false;
    if (search) {
      const s = search.toLowerCase();
      return (e.title || '').toLowerCase().includes(s) || (e.description || '').toLowerCase().includes(s) || (e.tags || []).some(t => t.toLowerCase().includes(s));
    }
    return true;
  });

  const byType = {};
  evidence.forEach(e => { byType[e.evidence_type] = (byType[e.evidence_type] || 0) + 1; });

  return (
    <div className="space-y-4" data-testid="evidence-locker">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold text-slate-700">Evidence Locker ({evidence.length} items)</h3>
        <Button size="sm" className="bg-slate-900 hover:bg-slate-800 gap-1 text-xs" onClick={() => setShowAdd(true)} data-testid="add-evidence-btn"><Plus className="h-3 w-3" /> Add Evidence</Button>
      </div>

      {/* Type Summary */}
      <div className="flex flex-wrap gap-1.5">
        {Object.entries(byType).map(([type, count]) => (
          <Badge key={type} className={`text-[10px] cursor-pointer ${filterType === type ? 'ring-2 ring-slate-900' : ''} ${TYPE_COLORS[type] || 'bg-slate-100'}`} onClick={() => setFilterType(filterType === type ? '' : type)}>
            {type.replace(/_/g, ' ')} ({count})
          </Badge>
        ))}
        {filterType && <Badge variant="outline" className="text-[10px] cursor-pointer" onClick={() => setFilterType('')}>Clear</Badge>}
      </div>

      {/* Search */}
      <div className="relative max-w-xs">
        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
        <Input placeholder="Search evidence..." value={search} onChange={e => setSearch(e.target.value)} className="pl-8" data-testid="evidence-search" />
      </div>

      {filtered.length === 0 ? (
        <Card><CardContent className="p-8 text-center"><Lock className="h-10 w-10 text-slate-300 mx-auto mb-2" /><p className="text-sm text-slate-500">{evidence.length === 0 ? 'No evidence added yet' : 'No evidence matching filters'}</p></CardContent></Card>
      ) : (
        <div className="space-y-2">
          {filtered.map(e => {
            const Icon = TYPE_ICONS[e.evidence_type] || File;
            return (
              <Card key={e.id} data-testid={`evidence-${e.id}`}>
                <CardContent className="p-3 flex items-start gap-3">
                  <div className={`w-9 h-9 rounded flex items-center justify-center shrink-0 ${TYPE_COLORS[e.evidence_type] || 'bg-slate-100'}`}>
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="font-mono text-[10px] text-slate-400">{e.evidence_ref}</span>
                      <h4 className="text-sm font-medium text-slate-800 truncate">{e.title}</h4>
                      {e.privileged_flag && <Badge className="bg-slate-800 text-white text-[8px] gap-0.5"><Shield className="h-2 w-2" /> Privileged</Badge>}
                    </div>
                    {e.description && <p className="text-xs text-slate-500 line-clamp-1">{e.description}</p>}
                    <div className="flex items-center gap-2 mt-1">
                      <Badge className={`text-[9px] ${TYPE_COLORS[e.evidence_type]}`}>{e.evidence_type.replace(/_/g, ' ')}</Badge>
                      {e.date_of_evidence && <span className="text-[10px] text-slate-400">{e.date_of_evidence}</span>}
                      {(e.tags || []).map(tag => <Badge key={tag} variant="outline" className="text-[8px] gap-0.5"><Tag className="h-2 w-2" />{tag}</Badge>)}
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:text-red-500" onClick={() => handleDelete(e.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="sm:max-w-md">
          <DialogTitle className="font-barlow font-bold uppercase">Add Evidence</DialogTitle>
          <div className="space-y-3 mt-2">
            <div><Label className="text-xs">Title *</Label><Input value={form.title} onChange={e => setForm(f => ({...f, title: e.target.value}))} placeholder="e.g. Payment Certificate No. 12" data-testid="evidence-title-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Type</Label>
                <Select value={form.evidence_type} onValueChange={v => setForm(f => ({...f, evidence_type: v}))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{EVIDENCE_TYPES.map(t => <SelectItem key={t} value={t}>{t.replace(/_/g, ' ')}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Date of Evidence</Label><Input type="date" value={form.date_of_evidence} onChange={e => setForm(f => ({...f, date_of_evidence: e.target.value}))} /></div>
            </div>
            <div><Label className="text-xs">Linked Issue</Label>
              <Select value={form.issue_id} onValueChange={v => setForm(f => ({...f, issue_id: v}))}>
                <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {(issues || []).map(i => <SelectItem key={i.id} value={i.id}>{i.issue_ref}: {i.issue_title}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Description</Label><Textarea value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))} rows={2} placeholder="Describe the evidence..." /></div>
            <div><Label className="text-xs">Tags (comma-separated)</Label><Input value={form.tags} onChange={e => setForm(f => ({...f, tags: e.target.value}))} placeholder="e.g. payment, certificate, valuation" /></div>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.privileged_flag} onChange={e => setForm(f => ({...f, privileged_flag: e.target.checked}))} className="rounded" /> Privileged</label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
            <Button className="bg-slate-900 hover:bg-slate-800" onClick={handleAdd} data-testid="submit-evidence"><Plus className="h-4 w-4 mr-1" /> Add Evidence</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
